import { useBoardValue } from "frontend/state/board-state";
import ActiveBoardUser from "frontend/user-presence/active-board-user";
import { useAtom, useAtomValue } from "jotai";
import { useCollaboratorIDs } from "subscriptions";
import styles from "./user-actions.module.css";
import { useRef } from "react";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { SidepanelType, sidePanelType, syncServiceAtom } from "state-atoms";

const Trigger = ({ reps }: { reps: any }) => {
  const syncService = useAtomValue(syncServiceAtom);
  const collaboratorsIds = useCollaboratorIDs(reps, true);
  if (collaboratorsIds.length === 0) {
    return null;
  }
  return (
    <div className={styles.users}>
      {collaboratorsIds.slice(0, 2).map((id) => (
        <ActiveBoardUser key={id} clientID={id} syncService={syncService} variant="cim" />
      ))}
    </div>
  );
};

const UserTrigger = () => {
  const [{ user }] = useBoardValue();
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
  const isUserActionsOpen = openedSidePanel === SidepanelType.userView;

  return (
    <div
      className={styles.userTrigger}
      data-open={isUserActionsOpen}
      onClick={() => setOpenedSidePanel(SidepanelType.userView)}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 41 25" width="41px" height="25px">
        {/* Background rounded rectangle */}
        <rect x="0.5" y="0.5" width="40" height="24" rx="12" fill="#FFF" stroke="#EBEDF3" strokeWidth="1" />

        {/* Profile image mask */}
        <defs>
          <mask id="profileMask">
            <rect x="0" y="0" width="41" height="25" fill="black" />
            <circle cx="12.5" cy="12.5" r="11" fill="white" />
          </mask>
        </defs>

        {/* Profile image positioned on the left */}
        <image
          x="1.5"
          y="1.5"
          width="22"
          height="22"
          href={user?.isAnonymous ? user?.avatar ?? "" : user?.photoURL ?? ""}
          mask="url(#profileMask)"
          preserveAspectRatio="xMidYMid slice"
        />

        {/* Chevron positioned on the right and centered vertically */}
        <g transform="translate(29, 14.5)">
          <path d="M 0 -2.5 L 2.5 0" stroke="#1E1E1E" strokeLinecap="round" strokeWidth="1" />
          <path d="M 5 -2.5 L 2.5 0" stroke="#1E1E1E" strokeLinecap="round" strokeWidth="1" />
        </g>
      </svg>
    </div>
  );
};

export const UserActions = ({ reps }: { reps: any }) => {
  const collaboratorsIds = useCollaboratorIDs(reps, true);
  const iconRef = useRef<HTMLDivElement>(null);
  const iconHover = useHover(iconRef);

  const openedSidePanel = useAtomValue(sidePanelType);
  const isUserActionsOpen = openedSidePanel === SidepanelType.userView;

  return (
    <div className={styles.userActions} ref={iconRef}>
      <Trigger reps={reps} />
      <UserTrigger />
      {!isUserActionsOpen && iconHover && (
        <Tooltip
          design="monday"
          label={collaboratorsIds.length >= 1 ? `${collaboratorsIds.length + 1} collaborators` : "Users"}
          relativeTo={iconRef}
          side={"bottom"}
          customStyle={{ borderRadius: 3 }}
        />
      )}
    </div>
  );
};
